<template>
    <div class="report-edit main-bg-item-dark-color">
        <div class="report-edit-container">
            <div class="report-edit-header">
                <div class="report-edit-header--title">
                    {{ $t('report.editPage.title') }}
                </div>
                <div class="flex">
                    <base-button
                        @click="onReportDelete"
                        type="danger"
                        outline
                        square
                        size="md"
                        class="report-edit-header--button report-edit-header--button-delete"
                    >
                        <template v-slot:icon>
                            <IconDelete />
                        </template>
                    </base-button>
                    <base-button
                        outline
                        fixed-width="w-37"
                        @click="onModalConfirm"
                        class="report-edit-header--button"
                    >
                        <div class="flex items-center">
                            <IconDiscard class="mx-1"/>
                            <span class="mx-1 text-base font-bold">{{ $t('common.cancel') }}</span>
                        </div>
                    </base-button>
                    <base-button
                        @click="saveChanges"
                        data-test-name="wizard-finish"
                        type="primary"
                        class="report-edit-header--button"
                    >
                        <IconSave class="mx-2" />
                        {{ $t('general.saveChanges') }}
                    </base-button>
                </div>
            </div>
            <div class="report-edit-fields main-bg-light">
                <div class="report-edit-fields-item">
                    <div class="report-label">
                        <i class="vc-icon-name label-icon text-primary" />{{ $t('report.name') }}<span class="require-icon">*</span>
                    </div>
                    <el-input
                        v-model="report.ReportName"
                        :placeholder="$t('report.name')"
                        type="text"
                    />
                    <div class="el-form-item__error" v-show="clickedOnSaveBtn && checkIfValueIsEmpty(report.ReportName)">
                        {{ $t('validation.error.fieldIsRequired') }}
                    </div>
                </div>
                <div class="report-edit-fields-layout">
                    <LayoutSelect class="mt-4"
                        :label="$t('report.layout')"
                        :active-layout="{ LayoutID: report.LayoutID }"
                        @on-chose-layout="onChoseLayout"
                    />
                </div>
                <div class="report-edit-fields-language">
                    <div class="report-label">
                        <i class="vc-icon-country label-icon text-primary" />
                        {{ $t('report.language') }}
                    </div>
                    <el-select v-model="report.ReportLanguageID" class="w-full">
                        <el-option
                            v-for="(language, index) in languageList"
                            :key="`lang-${index}`"
                            :label="language.LanguageName"
                            :value="language.LanguageID"
                            class="language-item"
                        >
                        </el-option>
                    </el-select>
                </div>
                <div class="">
                    <div class="report-label">
                        <i class="vc-icon-login-status label-icon text-primary" /> {{ $t('report.status') }}
                    </div>
                    <div class="flex items-center h-10">
                        <el-switch
                            v-model="report.ReportStatusID"
                            :active-text="report.ReportStatusID === REPORT_STATUS_ID_ENABLED ? $t('report.wizard.enabled') : $t('report.switch.disable')"
                            inactive-text=""
                            :active-value="1"
                            :inactive-value="2"
                        />
                    </div>
                </div>
                <div v-if="report.ReportConf" class="timezone-block">
                    <div class="flex timezone-block">
                        <div class="flex items-end mx-4 pb-2">
                            <el-checkbox v-model="report.ReportConf.timezone.timezoneStatus" :true-label="1" :false-label="0">
                                {{ $t('report.timezone.status') }}
                            </el-checkbox>
                        </div>
                        <div class="timezone-select">
                            <div class="mb-2 flex items-center" v-if="report.ReportConf.timezone.timezoneStatus">
                                <i class="vc-icon-name label-icon text-primary text-xl" />
                                <span class="main-text-color">
                                    {{ $t('general.timezone') }}
                                </span>
                            </div>
                            <el-select v-if="report.ReportConf && report.ReportConf.timezone.timezoneStatus" :placeholder="$t('general.timezone')" filterable v-model="report.ReportConf.timezone.timezoneName" class="select-type">
                                <el-option :key="option.value" :value="option.value" :label="option.label" v-bind="option" v-for="option in timezones"/>
                            </el-select>
                        </div>
                    </div>
                </div>
            </div>
            <div class="report-edit-tabs">
                <tab-card
                    :data="dataTabs"
                    :active-tab.sync="activeTab"
                    class="border-t md:border-0 mt-7"
                    data-test-name="report-item--edit"
                    :white-bg="true"
                >
                    <template slot="widgets">
                        <wrapper-widget-table showReorderButton :reportId="reportId" edit class="p-8" :reportData="report" @updateReportData="updateReportData" />
                    </template>
                    <template slot="schedule-list">
                        <schedule-list edit :reportData="report" @updateReportTriggerList="updateReportTriggerList" />
                    </template>
                </tab-card>
            </div>
        </div>
        <delete-dialog
            v-if="showDeleteDialog"
            :visible.sync="showDeleteDialog"
            :config="{descriptionIcon: ''}"
            :description="$t('report.deleteReportConfirmation')"
            @on-close="onCancelDelete"
            @on-cancel="onCancelDelete"
            @on-confirm="deleteReport"
        />
        <confirm-dialog
            v-if="showConfirmDialog"
            :visible.sync="showConfirmDialog"
            :config="{descriptionIcon: ''}"
            :description="$t('common.confirm.question', { action: 'confirm' })"
            @on-close="cancel"
            @on-cancel="cancel"
            @on-confirm="onCancelConfirm"
        />
    </div>
</template>

<script>
import {reportApi} from '@/modules/reports/services/reportService'
import cloneDeep from 'lodash/cloneDeep'
import {Checkbox, Notification, Option, Select} from 'element-ui'
import {REPORT_STATUS_ID_DELETE, REPORT_STATUS_ID_ENABLED} from '@/enum/reportStatuses'
import momentTimezone from 'moment-timezone'
import {languagesEnum} from "@/enum/languages.enum";

export default {
    name: 'edit-report-page',
    components: {
        DeleteDialog: () => import('@/components/Dialogs/DeleteDialog'),
        ConfirmDialog: () => import('@/components/Dialogs/ConfirmDialog'),
        ScheduleList: () => import('@/modules/reports/components/ScheduleList.vue'),
        TabCard: () => import('@/modules/reports/components/TabCard.vue'),
        WrapperWidgetTable: () => import('@/modules/reports/components/WrapperWidgetTable.vue'),
        LayoutSelect: () => import('@/views/common/LayoutSelect'),
        [Select.name]: Select,
        [Option.name]: Option,
        [Checkbox.name]: Checkbox
    },
    props: {
        report: {
            type: Object,
            default: () => ({})
        },
        reportId: {
            type: [Number, String],
            default: ''
        }
    },
    watch: {
        '$route': {
            immediate: true,
            handler (newVal, oldVal) {
                if (this.copyOfReportData && newVal.name === "reports-list" && oldVal.name === "reports-edit") {

                    const newReportData = cloneDeep(this.report)
                    if (newReportData.ReportItemList.length) {
                        newReportData.ReportItemList = newReportData.ReportItemList.map(el => {
                            delete el.index
                            return el
                        })
                    }

                    if (JSON.stringify(this.copyOfReportData.ReportTriggerList) !== JSON.stringify(newReportData.ReportTriggerList) || JSON.stringify(this.copyOfReportData.ReportItemList) !== JSON.stringify(newReportData.ReportItemList)) {
                        this.$emit('reload-report-list')
                    }
                }
            }
        },
        'report.ReportConf.timezone.timezoneStatus' (val) {
            if (!val) {
                this.report.ReportConf.timezone.timezoneName = null
                this.report.ReportConf.timezone.GmtOffset = this.currentAccount.GmtOffset
            }
        },
        'report.ReportConf.timezone.timezoneName' (val) {
            if (val) {
                const GmtOffset = momentTimezone().tz(val).utcOffset() * 3600
                this.report.ReportConf.timezone.GmtOffset = GmtOffset / 60
            }
        }
    },
    data () {
        return {
            showDeleteDialog: false,
            reportToDelete: null,
            showConfirmDialog: false,
            copyOfReportData: null,
            activeTab: 'widgets',
            dataTabs: [
                {
                    title: 'widget.widgets',
                    name: 'widgets',
                    icon: 'vc-icon-settings'
                },
                {
                    title: 'report.schedules',
                    name: 'schedule-list',
                    icon: 'vc-icon-schedule-calendar'
                }
            ],
            clickedOnSaveBtn: false,
            REPORT_STATUS_ID_ENABLED
        }
    },
    methods: {
        onReportDelete () {
            this.reportToDelete = this.$route.params.id
            if (!this.reportToDelete) return
            this.showDeleteDialog = true
        },
        async deleteReport() {
            try {
                await this.$store.dispatch('dashboards/setContentLoading', true)
                const payload = {
                    ReportID: this.reportToDelete,
                    ReportStatusID: REPORT_STATUS_ID_DELETE
                }
                await reportApi.changeStatus(payload)
                this.reportToDelete = null
                this.showDeleteDialog = false
            } catch (err) {
                console.error(err)
            } finally {
                await this.$store.dispatch('dashboards/setContentLoading', false)
                this.$emit('on-reload-data-reports-list')
            }
        },
        onCancelDelete() {
            this.reportToDelete = null
            this.showDeleteDialog = false
        },
        onModalConfirm () {
            const copyOfGetReportData = cloneDeep(this.report)

            if (this.copyOfReportData.ReportName !== copyOfGetReportData.ReportName ||
                this.copyOfReportData.ReportStatusID !== copyOfGetReportData.ReportStatusID ||  
                this.copyOfReportData.LayoutID !== copyOfGetReportData.LayoutID
            ) {
                this.showConfirmDialog = true
            } else {
                this.$emit('on-cancel')
            }
        },
        onCancelConfirm () {
            this.$emit('on-cancel')
        },
        cancel() {
            this.showConfirmDialog = false
        },
        saveChanges () {
            this.clickedOnSaveBtn = true
            if (this.checkIfValueIsEmpty(this.report.ReportName)) {
                return
            }
            try {
                reportApi.update(this.report)
                Notification.success(this.$t('general.notification.success'))
            } finally {
                this.clickedOnSaveBtn = false
                this.$emit('update-report-item', cloneDeep(this.report))
            }
        },
        checkIfValueIsEmpty (value) {
            return value === '' || value === null
        },
        onChoseLayout(layout) {
            this.report.LayoutID = layout.LayoutID
        },
        updateReportData (data) {
            this.report.ReportItemList = data.ReportItemList
        },
        updateReportTriggerList () {
            this.$emit('update-report-item', cloneDeep(this.report))
        }
    },
    mounted () {
        this.copyOfReportData = cloneDeep(this.report)
    },
    computed: {
        languageList () {
            return [ ...languagesEnum ]
        },
        timezones () {
            const timeZones = momentTimezone.tz.names();
            const offsetTmz=[];

            for(const i in timeZones) {
                offsetTmz.push({
                    value: timeZones[i],
                    label: " (GMT"+momentTimezone.tz(timeZones[i]).format('Z')+") " + timeZones[i]
                });
            }

            return offsetTmz
        },
        currentAccount() {
            return this.$store.getters['entities/getCurrentAccount']
        }
    }
}
</script>

<style lang="scss" scoped>
.report-label {
    @apply mb-2 flex items-center;
    color: var(--mainTextColor-color);
}
.report-edit {
    &-header {
        @apply mb-4 flex items-center justify-between;
        &--title {
            @apply text-2xl;
            color: var(--mainTextColor-color);
        }
        [dir="ltr"] &--button:not(:last-child){
            @apply mr-8;
        }
        [dir="rtl"] &--button:not(:last-child) {
            @apply ml-8;
        }
        &--button-delete {
            @apply h-10 w-10;
        }
        &--button {
            @apply h-10;
        }
    }
    &-fields {
        @apply flex flex-wrap shadow-base rounded-md px-8 py-6 mb-6;
        &-item {
            max-width: 392px;
            width: 100%;
            @apply mb-2 relative;
        }
        [dir="ltr"] &-layout {
            @apply mr-8;
        }
        [dir="rtl"] &-layout {
            @apply ml-8;
        }
        [dir="ltr"] &-language {
            @apply mr-8;
        }
        [dir="rtl"] &-language {
            @apply ml-8;
        }
        [dir="ltr"] &-item:not(:last-child) {
            @apply mr-8;
        }
        [dir="rtl"] &-item:not(:last-child) {
            @apply ml-8;
        }
    }
}
[dir="rtl"] .require-icon {
    @apply text-primary mr-2;
}
[dir="ltr"] .require-icon  {
    @apply text-primary ml-2;
}
.el-form-item__error {
    @apply pt-2;
}
[dir="rtl"] .label-icon {
    @apply ml-2;
}
[dir="ltr"] .label-icon  {
    @apply mr-2;
}
::v-deep .el-switch__label.is-active {
    @apply text-primary;
}
::v-deep .el-switch.is-checked .el-switch__core {
    border-color: var(--primary-color);
    background-color: var(--primary-color);
}
::v-deep .el-switch__label {
    color: var(--mainTextColor-color);
}
</style>

<style lang="scss">
[dir="rtl"] .report-edit .el-switch__label {
    @apply mr-2;
}
[dir="ltr"] .report-edit .el-switch__label {
    @apply ml-2;
}
.layout-select-label {
    @apply text-base font-medium mx-2;
}
.layout-select-container {
    @apply mb-2;
}
.layout-selection {
    margin-top: 0px !important;
}
.timezone-block {
    height: 70px;
}
</style>
